import type { DrawingKind } from "lightweight-charts-drawing"

// The drawing tools in TradingView's toolbar groups, names and hotkeys. Generated from
// lightweight-charts-drawing v0.6.2 docs/TOOLS.md (MIT); 85 tools.

export interface DrawingTool {
  readonly name: string
  readonly kind: DrawingKind
  readonly hotkey?: string
}

export interface DrawingToolGroup {
  /** Toolbar button, e.g. "Trend tools". */
  readonly group: string
  /** Heading inside the flyout, e.g. "Channels". */
  readonly subgroup: string
  readonly tools: ReadonlyArray<DrawingTool>
}

export const DRAWING_TOOL_GROUPS: ReadonlyArray<DrawingToolGroup> = [
  {
    group: "Trend tools",
    subgroup: "Lines",
    tools: [
      {
        name: "Trendline",
        kind: "trend-line",
        hotkey: "Alt + T"
      },
      {
        name: "Ray",
        kind: "ray"
      },
      {
        name: "Info line",
        kind: "info-line"
      },
      {
        name: "Extended line",
        kind: "extended-line"
      },
      {
        name: "Trend angle",
        kind: "trend-angle"
      },
      {
        name: "Horizontal line",
        kind: "horizontal-line",
        hotkey: "Alt + H"
      },
      {
        name: "Horizontal ray",
        kind: "horizontal-ray",
        hotkey: "Alt + J"
      },
      {
        name: "Vertical line",
        kind: "vertical-line",
        hotkey: "Alt + V"
      },
      {
        name: "Cross line",
        kind: "cross-line",
        hotkey: "Alt + C"
      }
    ]
  },
  {
    group: "Trend tools",
    subgroup: "Channels",
    tools: [
      {
        name: "Parallel channel",
        kind: "parallel-channel"
      },
      {
        name: "Regression trend",
        kind: "regression-trend"
      },
      {
        name: "Flat top/bottom",
        kind: "flat-top-bottom"
      },
      {
        name: "Disjoint channel",
        kind: "disjoint-channel"
      }
    ]
  },
  {
    group: "Trend tools",
    subgroup: "Pitchforks",
    tools: [
      {
        name: "Pitchfork",
        kind: "pitchfork"
      },
      {
        name: "Schiff pitchfork",
        kind: "schiff-pitchfork"
      },
      {
        name: "Modified Schiff pitchfork",
        kind: "modified-schiff-pitchfork"
      },
      {
        name: "Inside pitchfork",
        kind: "inside-pitchfork"
      }
    ]
  },
  {
    group: "Gann and Fibonacci tools",
    subgroup: "Fibonacci",
    tools: [
      {
        name: "Fib retracement",
        kind: "fib-retracement",
        hotkey: "Alt + F"
      },
      {
        name: "Trend-based fib extension",
        kind: "trend-based-fib-extension"
      },
      {
        name: "Fib channel",
        kind: "fib-channel"
      },
      {
        name: "Fib time zone",
        kind: "fib-time-zone"
      },
      {
        name: "Fib speed resistance fan",
        kind: "fib-speed-resistance-fan"
      },
      {
        name: "Trend-based fib time",
        kind: "trend-based-fib-time"
      },
      {
        name: "Fib circles",
        kind: "fib-circles"
      },
      {
        name: "Fib spiral",
        kind: "fib-spiral"
      },
      {
        name: "Fib speed resistance arcs",
        kind: "fib-speed-resistance-arcs"
      },
      {
        name: "Fib wedge",
        kind: "fib-wedge"
      },
      {
        name: "Pitchfan",
        kind: "pitchfan"
      }
    ]
  },
  {
    group: "Gann and Fibonacci tools",
    subgroup: "Gann",
    tools: [
      {
        name: "Gann box",
        kind: "gann-box"
      },
      {
        name: "Gann square fixed",
        kind: "gann-square-fixed"
      },
      {
        name: "Gann square",
        kind: "gann-square"
      },
      {
        name: "Gann fan",
        kind: "gann-fan"
      }
    ]
  },
  {
    group: "Patterns",
    subgroup: "Chart patterns",
    tools: [
      {
        name: "XABCD pattern",
        kind: "xabcd-pattern"
      },
      {
        name: "Cypher pattern",
        kind: "cypher-pattern"
      },
      {
        name: "Head and shoulders",
        kind: "head-and-shoulders"
      },
      {
        name: "ABCD pattern",
        kind: "abcd-pattern"
      },
      {
        name: "Triangle pattern",
        kind: "triangle-pattern"
      },
      {
        name: "Three drives pattern",
        kind: "three-drives-pattern"
      }
    ]
  },
  {
    group: "Patterns",
    subgroup: "Elliott waves",
    tools: [
      {
        name: "Elliott impulse wave (1\u00b72\u00b73\u00b74\u00b75)",
        kind: "elliott-impulse"
      },
      {
        name: "Elliott correction wave (A\u00b7B\u00b7C)",
        kind: "elliott-correction"
      },
      {
        name: "Elliott triangle wave (A\u00b7B\u00b7C\u00b7D\u00b7E)",
        kind: "elliott-triangle"
      },
      {
        name: "Elliott double combo wave (W\u00b7X\u00b7Y)",
        kind: "elliott-double-combo"
      },
      {
        name: "Elliott triple combo wave (W\u00b7X\u00b7Y\u00b7X\u00b7Z)",
        kind: "elliott-triple-combo"
      }
    ]
  },
  {
    group: "Patterns",
    subgroup: "Cycles",
    tools: [
      {
        name: "Cyclic lines",
        kind: "cyclic-lines"
      },
      {
        name: "Time cycles",
        kind: "time-cycles"
      },
      {
        name: "Sine line",
        kind: "sine-line"
      }
    ]
  },
  {
    group: "Forecasting and measurement tools",
    subgroup: "Forecasting",
    tools: [
      {
        name: "Long position",
        kind: "long-position"
      },
      {
        name: "Short position",
        kind: "short-position"
      },
      {
        name: "Position forecast",
        kind: "position-forecast"
      },
      {
        name: "Bars pattern",
        kind: "bar-pattern"
      },
      {
        name: "Ghost feed",
        kind: "ghost-feed"
      },
      {
        name: "Sector",
        kind: "sector"
      }
    ]
  },
  {
    group: "Forecasting and measurement tools",
    subgroup: "Volume-based",
    tools: [
      {
        name: "Anchored VWAP",
        kind: "anchored-vwap"
      },
      {
        name: "Fixed range volume profile",
        kind: "fixed-range-volume-profile"
      },
      {
        name: "Anchored volume profile",
        kind: "anchored-volume-profile"
      }
    ]
  },
  {
    group: "Forecasting and measurement tools",
    subgroup: "Measurers",
    tools: [
      {
        name: "Price range",
        kind: "price-range"
      },
      {
        name: "Date range",
        kind: "date-range"
      },
      {
        name: "Date and price range",
        kind: "date-and-price-range"
      }
    ]
  },
  {
    group: "Geometric shapes",
    subgroup: "Brushes",
    tools: [
      {
        name: "Brush",
        kind: "brush"
      },
      {
        name: "Highlighter",
        kind: "highlighter"
      }
    ]
  },
  {
    group: "Geometric shapes",
    subgroup: "Arrows",
    tools: [
      {
        name: "Arrow marker",
        kind: "arrow-marker"
      },
      {
        name: "Arrow",
        kind: "arrow"
      },
      {
        name: "Arrow mark up",
        kind: "arrow-mark-up"
      },
      {
        name: "Arrow mark down",
        kind: "arrow-mark-down"
      }
    ]
  },
  {
    group: "Geometric shapes",
    subgroup: "Shapes",
    tools: [
      {
        name: "Rectangle",
        kind: "rectangle",
        hotkey: "Alt + Shift + R"
      },
      {
        name: "Rotated rectangle",
        kind: "rotated-rectangle"
      },
      {
        name: "Path",
        kind: "path"
      },
      {
        name: "Circle",
        kind: "circle"
      },
      {
        name: "Ellipse",
        kind: "ellipse"
      },
      {
        name: "Polyline",
        kind: "polyline"
      },
      {
        name: "Triangle",
        kind: "triangle"
      },
      {
        name: "Arc",
        kind: "arc"
      },
      {
        name: "Curve",
        kind: "curve"
      },
      {
        name: "Double curve",
        kind: "double-curve"
      }
    ]
  },
  {
    group: "Annotation tools",
    subgroup: "Text and notes",
    tools: [
      {
        name: "Text",
        kind: "text"
      },
      {
        name: "Note",
        kind: "note"
      },
      {
        name: "Price note",
        kind: "price-note"
      },
      {
        name: "Pin",
        kind: "pin"
      },
      {
        name: "Table",
        kind: "table"
      },
      {
        name: "Callout",
        kind: "callout"
      },
      {
        name: "Comment",
        kind: "comment"
      },
      {
        name: "Price label",
        kind: "price-label"
      },
      {
        name: "Signpost",
        kind: "signpost"
      },
      {
        name: "Flag mark",
        kind: "flag-mark"
      }
    ]
  },
  {
    group: "Annotation tools",
    subgroup: "Content",
    tools: [
      {
        name: "Image",
        kind: "image"
      }
    ]
  }
] as const satisfies ReadonlyArray<DrawingToolGroup>
